import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import find from 'lodash/find';
import uniqueId from 'lodash/uniqueId';
import Button from '@sharedComponents/Button/Button'
import { ButtonType } from '@sharedComponents/Button/types'
import { Category } from '../types';
import InputSearch from '../../InputSearch';
import WithUrl, { WithUrlProps } from '../../../Services/WithUrl';
import './Scss/filter.scss';

function formatNameForHash(input: string | null) {
    if (input === null) {
        return '#';
    }

    return (
        '#' +
        input
            .toLowerCase()
            .replace(/[^A-Za-z0-9\s/]/g, '')
            .split(' ')
            .join('-')
            .split('/')
            .join('-')
    );
}

function selectCategory(
    category: Category | null,
    setHash: Function,
    setSelectedCategoryId: Function,
    onChangeSelectedCategoryId?: Function
) {
    const newHash = `${formatNameForHash(category && category.name)}`;

    const categoryId = (category && category.id) || null;

    setHash(newHash);

    setSelectedCategoryId(categoryId);

    onChangeSelectedCategoryId && onChangeSelectedCategoryId(categoryId);
}

export type FilterProps = {
    categories: Category[]
    searchVal: string
    normalizedSearchVal: string
    onChangeSelectedCategoryId?: Function
    onChangeSearchVal?: Function
}

const Filter = (props: FilterProps & WithUrlProps) => {
    const { t } = useTranslation()

    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
        null
    )

    // Update filter when props.location changes (from WithUrl)
    useEffect(() => {
        const category = find(
            props.categories,
            category => formatNameForHash(category.name) === props.location.hash
        )

        const categoryId = (category && category.id) || null

        setSelectedCategoryId(categoryId)

        props.onChangeSelectedCategoryId &&
            props.onChangeSelectedCategoryId(categoryId)
    }, [props.location])

    // Listen for hashchange events in the browser and update filter accordingly
    useEffect(() => {
        const handleHashChange = () => {
            // Find the category for the new hash
            const category = find(
                props.categories,
                category => formatNameForHash(category.name) === window.location.hash
            );
            const categoryId = (category && category.id) || null;
            setSelectedCategoryId(categoryId);

            props.onChangeSelectedCategoryId && props.onChangeSelectedCategoryId(categoryId);
        };
        window.addEventListener('hashchange', handleHashChange);
        // Run once on mount in case hash is already set
        handleHashChange();
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [props.categories, props.onChangeSelectedCategoryId]);

    return (
        <div className='filter search-wrapper'>
            <div className='filter__search'>
                <InputSearch
                    id={uniqueId()}
                    label={t('faq.searchInputLabel')}
                    hideLabel={true}
                    value={props.searchVal}
                    onChange={(e: any) => {
                        props.onChangeSearchVal &&
                            props.onChangeSearchVal(e.target.value)
                    }}
                    placeholder={t('faq.searchInputPlaceholder')}
                />
            </div>

            <div className='filter__buttons'>
                <Button
                    text={t('faq.showAll')}
                    type={selectedCategoryId === null ? ButtonType.PRIMARY : ButtonType.SECONDARY}
                    onClick={selectCategory.bind(
                        null,
                        null,
                        props.setHash,
                        setSelectedCategoryId,
                        props.onChangeSelectedCategoryId
                    )}
                />

                {props.categories.map((category: Category, i: number) => {
                    const isSelected = category.id === selectedCategoryId;
                    const isDisabled = !!props.normalizedSearchVal && category.links.length === 0;

                    return (
                        <Button
                            key={i}
                            type={isSelected ? ButtonType.PRIMARY : ButtonType.SECONDARY}
                            onClick={selectCategory.bind(
                                null,
                                category,
                                props.setHash,
                                setSelectedCategoryId,
                                props.onChangeSelectedCategoryId
                            )}
                            disabled={isDisabled}
                            text={category.name}
                        />
                    );
                })}
            </div>
        </div>
    )
}

export default (props: FilterProps) => WithUrl(Filter, props);
